// src/components/global/Table/TableLayout.jsx
import React from 'react';
import { Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TableHeader from './TableHeader';
import TableFilterBar from './TableFilterBar';
import TableBulkToolbar from './TableBulkToolbar';
import TableBody from './TableBody';
import TableMenu from './TableMenu';

const TableLayout = (props) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const theme = useTheme();

    const {
        title,
        subtitle,
        search,
        onSearchChange,
        addButtonLabel,
        onAddClick,
        addButtonVisible = true,
        isLoading,
        error,
        items,
        meta,
        page,
        perPage,
        onPageChange,
        onPerPageChange,
        order,
        onRequestSort,
        isDeletingSingle,
        isArchive, // ✨ NEW
        selectedIds = [],
        onSelectAllClick,
        onRowCheckboxClick,
        onBulkDelete,
        isBulkDeleting = false,
        onBulkRestore,
        isBulkRestoring = false,
        bulkRestoreDisabled = false,
        columns,
        renderRow,
        menuAnchorEl,
        openMenu,
        onMenuOpen,
        onMenuClose,
        onMenuView,
        onMenuEdit,
        onMenuRestore,
        onMenuDelete,
        onMenuDuplicate,
        addButtonDisabled = false,
        addButtonTooltip,
        // Archive Toggle Props
        archiveButtonVisible = true,
        onArchiveToggle,
        menuDisableEdit = false,
        menuDisableRestore = false,
        menuDisableDelete = false,
        bulkDeleteDisabled,
        emptyLabelAr = 'لا توجد بيانات',
        emptyLabelEn = 'No data',
        loadErrorAr = 'حدث خطأ أثناء تحميل البيانات',
        loadErrorEn = 'Failed to load data',
        // ✨ New props for filters
        filters,
        onClearAllFilters,
        // Optional slot rendered in the header next to the Add button —
        // used for module-specific actions like CSV export.
        headerExtra,
    } = props;

    const enhancedRenderRow = (params) =>
        renderRow({
            ...params,
            selectedIds,
            onRowCheckboxClick,
            onMenuOpen,
        });

    return (
        <Box
            sx={{
                mt: 3,
                direction: isArabic ? 'rtl' : 'ltr',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: (t) => `1px solid ${t.palette.divider}`,
                    boxShadow: theme.palette.boxShadow,
                    bgcolor: theme.palette.background.card,
                }}
            >
                <TableHeader
                    title={title}
                    subtitle={subtitle}
                    search={search}
                    onSearchChange={onSearchChange}
                    addButtonLabel={addButtonLabel}
                    onAddClick={onAddClick}
                    addButtonVisible={!isArchive && addButtonVisible}
                    addButtonDisabled={addButtonDisabled}
                    addButtonTooltip={addButtonTooltip}
                    headerExtra={headerExtra}
                    // Archive Toggle
                    archiveButtonVisible={archiveButtonVisible}
                    isArchiveView={isArchive}
                    onArchiveToggle={onArchiveToggle}
                />

                {/* ✨ Filter Bar - Only shows if filters are provided */}
                {filters && filters.length > 0 && (
                    <TableFilterBar
                        filters={filters}
                        onClearAll={onClearAllFilters}
                    />
                )}

                <TableBulkToolbar
                    selectedCount={selectedIds?.length || 0}
                    onBulkDelete={onBulkDelete}
                    onBulkRestore={onBulkRestore}
                    isBulkDeleting={isBulkDeleting}
                    isBulkRestoring={isBulkRestoring}
                    bulkDeleteDisabled={bulkDeleteDisabled}
                    bulkRestoreDisabled={bulkRestoreDisabled}
                    isArchive={isArchive} // ✨ NEW
                />

                <TableBody
                    isLoading={isLoading}
                    error={error}
                    items={items}
                    meta={meta}
                    page={page}
                    perPage={perPage}
                    onPageChange={onPageChange}
                    onPerPageChange={onPerPageChange}
                    order={order}
                    onRequestSort={onRequestSort}
                    selectedIds={selectedIds}
                    onSelectAllClick={onSelectAllClick}
                    columns={columns}
                    renderRow={enhancedRenderRow}
                    emptyLabelAr={emptyLabelAr}
                    emptyLabelEn={emptyLabelEn}
                    loadErrorAr={loadErrorAr}
                    loadErrorEn={loadErrorEn}
                />

                <TableMenu
                    anchorEl={menuAnchorEl}
                    open={openMenu}
                    onClose={onMenuClose}
                    onView={onMenuView}
                    onEdit={!isArchive ? onMenuEdit : null}
                    onRestore={isArchive ? onMenuRestore : null}
                    onDelete={onMenuDelete}
                    onDuplicate={onMenuDuplicate}
                    isArchiveMode={isArchive}
                    disableEdit={menuDisableEdit}
                    disableRestore={menuDisableRestore}
                    disableDelete={menuDisableDelete}
                    isDeletingSingle={isDeletingSingle}
                />
            </Paper>
        </Box>
    );
};

export default TableLayout;